import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Body1,
  Caption1,
  Card,
  CardFooter,
  CardPreview,
} from '@fluentui/react-components';
import { ListItem } from '@fluentui/react-list-preview';
import { Video20Regular } from '@fluentui/react-icons';
import {
  getTimeDescription,
  VideoStatus,
  UnverifiedPlaceholderWithStyle,
  EncodingPlaceholderWithStyle,
  FailedPlaceholderWithStyle,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useVideoCardStyles } from '../../../home-page-styles/component-styles/home-page-video-asset-cards';

export const VideoAssetCard = ({
  asset, i18n, navigation, renameAsset, ariaSetsize, ariaPosinset, onClickCard,
}) => {
  const styles = useVideoCardStyles();
  const currentActivedCardId = useRef();

  const assetName = useMemo(() => (asset.name ? asset.name : asset.id), [asset.id, asset.name]);

  /* istanbul ignore next */
  const videoTimeCaption = useMemo(() => {
    if (asset.modifiedTime > asset.createdTime) {
      return getTimeDescription(asset.modifiedTime, i18n) ? `${i18n.getString(_TL_('Edited:'))} ${getTimeDescription(asset.modifiedTime, i18n)}` : '';
    }
    return getTimeDescription(asset.createdTime, i18n) ? `${i18n.getString(_TL_('Uploaded:'))} ${getTimeDescription(asset.createdTime, i18n)}` : '';
  }, [asset.modifiedTime, asset.createdTime, i18n]);

  /* istanbul ignore next */
  const videoCardPreview = useMemo(() => {
    let defaultContent = null;
    // status order: unverified => draft => active/invalid
    if (asset.status === VideoStatus.Active) {
      defaultContent = (
        <img
          key={asset.id}
          src={asset.thumbnailUrl}
          alt={`Preview of asset ${assetName}`}
          className={styles.videoCardThumbnail}
        />
      );
    } else if (asset.status === VideoStatus.Unverified) {
      defaultContent = <div className={styles.videoCardPreviewPlaceholder}><UnverifiedPlaceholderWithStyle placeholderContent={i18n.getString(_TL_('Downloading...'))} /></div>;
    } else if (asset.status === VideoStatus.Draft) {
      defaultContent = <div className={styles.videoCardPreviewPlaceholder}><EncodingPlaceholderWithStyle placeholderContent={i18n.getString(_TL_('Processing...'))} /></div>;
    } else if (asset.status === VideoStatus.Failed) {
      defaultContent = <div className={styles.videoCardPreviewPlaceholder}><FailedPlaceholderWithStyle placeholderContent={i18n.getString(_TL_('Invalid video'))} /></div>;
    }

    return defaultContent;
  }, [asset, assetName, i18n, styles.videoCardPreviewPlaceholder, styles.videoCardThumbnail]);

  const onCloseDetail = useCallback(() => {
    currentActivedCardId.current = null;
  }, []);

  const handleVideoAssetClick = useCallback((videoAsset) => {
    if (videoAsset.status !== VideoStatus.Active) {
      return;
    }
    onClickCard();
    currentActivedCardId.current = videoAsset.id;
    navigation.gotoAssetDetails({
      assetId: videoAsset.id, folderAssetType: videoAsset.folderAssetType, assets: [videoAsset], totalAssetCount: 1, renameAsset, onClose: onCloseDetail,
    });
  }, [navigation, onClickCard, onCloseDetail, renameAsset]);

  const handleVideoAssetKeyDown = useCallback((event, videoAsset) => {
    if (event.key === 'Enter') {
      handleVideoAssetClick(videoAsset);
      event.preventDefault();
    }
  }, [handleVideoAssetClick]);

  useEffect(() => {
    if (navigation && currentActivedCardId.current === asset.id) {
      navigation.updateAssetDetailsParams({ assets: [asset] });
    }
  }, [asset, navigation]);

  return (
    <ListItem
      aria-setsize={ariaSetsize}
      aria-posinset={ariaPosinset}
      aria-label={`Preview of asset ${assetName} ${videoTimeCaption}`}
      onClick={() => handleVideoAssetClick(asset)}
      onKeyDown={event => handleVideoAssetKeyDown(event, asset)}
      className={styles.videoCardListItem}
    >
      <Card
        className={styles.videoCard}
      >
        <CardPreview>
          {videoCardPreview}
        </CardPreview>
        <div className={styles.videoCardFooter}>
          <CardFooter className={styles.videoCardFooterContent}>
            <div className={styles.videoIconAndName}>
              <Video20Regular style={{ marginRight: '8px', fontSize: 20 }} />
              <Body1 className={styles.videoName} title={assetName}>{assetName}</Body1>
            </div>
            <div className={styles.videoCardCaption}>
              <Caption1>{videoTimeCaption || ''}</Caption1>
            </div>
          </CardFooter>
        </div>
      </Card>
    </ListItem>
  );
};

VideoAssetCard.propTypes = {
  asset: PropTypes.shape({
    name: PropTypes.string,
    createdTime: PropTypes.instanceOf(Date),
    modifiedTime: PropTypes.instanceOf(Date),
    id: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    url: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  navigation: PropTypes.shape({
    gotoAssetDetails: PropTypes.func.isRequired,
    updateAssetDetailsParams: PropTypes.func.isRequired,
  }).isRequired,
  renameAsset: PropTypes.func.isRequired,
};
